import type { FC } from 'react'
import { useRef } from 'react'

import type { TranslationLang, UpdateTranslationPayload } from '@skytech/types'
import { Textarea } from '@skytech/ui'
import { Button } from '@skytech/ui/Button'
import {
  LANGUAGE_FLAGS,
  LOCALE_WITH_NAMES,
  getEntries,
  getInitialTranslationsValues,
  getWhiteLabelForDealerStyle,
} from '@skytech/utils'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import type { FieldProps, FormikProps } from 'formik'
import { Field, Form, Formik } from 'formik'

import queries from '@/api/queries'
import translationResources from '@/api/translations'
import { useAppContext } from '@/App.context'
import { useEditTranslationsPrivilege } from '@/hooks/user'

import { useTranslation } from '../hooks'

import './EditTranslationsForm.styles.scss'

export const EditTranslationsForm: FC<{ translationId: string; onClose: () => void }> = ({
  translationId,
  onClose,
}) => {
  const translate = useTranslation()
  const queryClient = useQueryClient()
  const editTranslationsPrivilege = useEditTranslationsPrivilege()
  const { locale: userLocale, whiteLabel } = useAppContext()

  const formRef = useRef<FormikProps<Record<TranslationLang, string>> | null>(null)

  const { data: translationList } = useQuery(
    queries.translationList(),
    translationResources.getList,
    { staleTime: Infinity }
  )

  const translationsMutation = useMutation(translationResources.update, {
    onSuccess() {
      queryClient.invalidateQueries(queries.translationList())
    },
  })

  return (
    <Formik
      innerRef={formRef}
      initialValues={getInitialTranslationsValues({ translationList, translationId })}
      onSubmit={(formValues, { resetForm }) => {
        const payload: UpdateTranslationPayload = []
        getEntries(formValues).forEach(([key, val]) => {
          const isDirty = formRef.current?.getFieldMeta(key).initialValue !== val
          if (!isDirty) return
          payload.push({
            locale: key,
            id: translationId,
            text: val || '',
          })
          const updatedInitialValues = getInitialTranslationsValues({
            translationList,
            translationId,
          })
          payload.forEach(({ locale, text }) => {
            updatedInitialValues[locale] = text
          })
          resetForm({ values: updatedInitialValues })
        })

        translationsMutation.mutate(payload)
      }}
    >
      <div className="edit-translations">
        <h2>{`${translate('Edit translations for')} '${translationId}'`}</h2>

        <Form>
          <div className="language-inputs">
            {getEntries(LANGUAGE_FLAGS).map(([locale, flag]) => {
              const isEnabled =
                editTranslationsPrivilege === 'CAN_EDIT_ALL_LANG' || locale === userLocale
              return (
                <Field key={locale} name={locale}>
                  {({ field, meta }: FieldProps) => (
                    <Textarea
                      isFullWidth
                      className={locale === userLocale ? 'is-user-lang' : ''}
                      whiteLabel={getWhiteLabelForDealerStyle(whiteLabel)}
                      id={locale}
                      isDisabled={!isEnabled}
                      label={`${LOCALE_WITH_NAMES[locale]} ${flag}`}
                      marginBottom={16}
                      error={meta.error}
                      touched={meta.touched}
                      {...field}
                    />
                  )}
                </Field>
              )
            })}
          </div>

          <footer>
            <Button
              variant="tertiary"
              disabled={translationsMutation.isLoading}
              onClick={onClose}
              whiteLabel={getWhiteLabelForDealerStyle(whiteLabel)}
            >
              {translate('Cancel')}
            </Button>

            <Button
              type="submit"
              isLoading={translationsMutation.isLoading}
              whiteLabel={getWhiteLabelForDealerStyle(whiteLabel)}
            >
              {translate('Submit')}
            </Button>
          </footer>
        </Form>
      </div>
    </Formik>
  )
}
